import apiClient from "./apiClient";
import AppError from "../utils/AppError";
import InspectionFilters from "../models/InspectionFilters";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notifications";

const FilterService = {
  async getFilters(organisationId) {
    try {
      const { data } = await apiClient.get("/inspections/filters", {
        params: { organisation: organisationId },
      });
      const inspectionFilters = InspectionFilters.fromServerModel(
        data.filterModel
      );
      return { inspectionFilters, folderFilters: data.folderFilterModel || [] };
    } catch (error) {
      // Don't show notification for 403 - upload-only users don't have
      // permission to fetch inspection filters, which is expected. The
      // "no saved filter yet" empty state now returns a 200 with defaults
      // from the backend, so it no longer needs handling here.
      if (error.response?.status === 403) {
        throw error;
      }
      const appError = new AppError({
        title: "Failed to Fetch Filters",
        response: error.response,
        userMessage: "Could not retrieve filters.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async updateFilters(organisationId, inspectionFilters, silent = false) {
    try {
      const payload = {
        organisation: Number(organisationId),
        filterModel: inspectionFilters.toServerModel(),
      };
      const { data } = await apiClient.patch("/inspections/filters", payload);
      if (!silent)
        showSuccessNotification(
          "Success",
          "Inspection filters updated successfully!"
        );
      const updatedInspectionFilters = InspectionFilters.fromServerModel(
        data.filterModel
      );
      return {
        inspectionFilters: updatedInspectionFilters,
        folderFilters: data.folderFilterModel || [],
      };
    } catch (error) {
      console.error("Filter Service Error", error);
      const appError = new AppError({
        title: "Failed to Update Filters",
        response: error.response,
        userMessage: "Could not update filters.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async updateFolderFilters(
    organisationId,
    folderFiltersObj,
    folderTree,
    silent = false
  ) {
    try {
      const prunedFilters = folderFiltersObj.prune(folderTree);
      const payload = {
        organisation: Number(organisationId),
        folderFilterModel: prunedFilters.toServerModel(),
      };
      const { data } = await apiClient.patch("/inspections/filters", payload);
      if (!silent)
        showSuccessNotification(
          "Success",
          "Folder filters updated successfully!"
        );
      return { folderFilters: data.folderFilterModel || [] };
    } catch (error) {
      console.error("Filter Service Error", error);
      const appError = new AppError({
        title: "Failed to Update Folder Filters",
        response: error.response,
        userMessage: "Could not update folder filters.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },
};

export default FilterService;
